import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import { ExclamationTriangleFill } from 'react-bootstrap-icons'
import { t } from 'i18next'
import cashIcon from 'Assets/Images/PaymentMethods/cash.svg'
import cardIcon from 'Assets/Images/PaymentMethods/card.svg'
import yappyIcon from 'Assets/Images/PaymentMethods/yappy.svg'
import cmfIcon from 'Assets/Images/PaymentMethods/cmf.svg'
import flatPointsLogo from 'Assets/Images/Clients/flat_points.svg'
import { Formatter } from 'Utils/Formatter'
import { regexCellPhone } from 'Utils/constants'
import { sanitizeCellPhone } from 'Utils/functions'
import { PAYMENT_OPTIONS, PAYMENT_METHOD_IDS } from './constants/paymentOptions'
import './PaymentMethodModal.css'

const PAYMENT_ICON_MAP = {
  cash: cashIcon,
  card: cardIcon,
  yappy: yappyIcon,
  cmf: cmfIcon,
  puntos_gordos: flatPointsLogo,
}

const NUMERIC_INPUT_RE = /^\d*\.?\d*$/
const VALID_AMOUNT_RE = /^\d+(\.\d{1,2})?$/

function PaymentOptionIcon({ iconType }) {
  const src = PAYMENT_ICON_MAP[iconType]
  if (!src) return null

  return (
    <img
      src={src}
      alt=""
      className="payment-option-card__logo"
      aria-hidden="true"
    />
  )
}

PaymentOptionIcon.propTypes = {
  iconType: PropTypes.string.isRequired,
}

function PaymentOptionCard({ option, isSelected, onSelect, disabled }) {
  return (
    <button
      type="button"
      className={`payment-option-card${
        isSelected ? ' payment-option-card--selected' : ''
      }${option.fullWidth ? ' payment-option-card--full-width' : ''}${
        disabled ? ' payment-option-card--disabled' : ''
      }`}
      onClick={() => !disabled && onSelect(option.id)}
      aria-pressed={isSelected}
      aria-disabled={disabled}
      disabled={disabled}
      data-testid={`btn-payment-method-${option.id}`}>
      <PaymentOptionIcon iconType={option.iconType} />
      <span className="payment-option-card__label">{t(option.label)}</span>
    </button>
  )
}

PaymentOptionCard.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    iconType: PropTypes.string.isRequired,
    fullWidth: PropTypes.bool,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

const fullWidthOption = PAYMENT_OPTIONS.find(o => o.fullWidth)
const gridOptions = PAYMENT_OPTIONS.filter(o => !o.fullWidth)

function PaymentMethodModal({ open, onClose, totalAmount, phone, onConfirm }) {
  const formattedTotalAmount = useMemo(
    () =>
      totalAmount == null
        ? ''
        : String(
            Formatter.Money(totalAmount, {
              rounded: true,
              decimalPrecision: 2,
              completeWithZero: true,
              withFormat: false,
            })
          ),
    [totalAmount]
  )
  const formattedPhone = useMemo(() => sanitizeCellPhone(phone), [phone])

  const [selectedMethod, setSelectedMethod] = useState(PAYMENT_METHOD_IDS.YAPPY)
  const [amount, setAmount] = useState(formattedTotalAmount)
  const [phoneInput, setPhoneInput] = useState(formattedPhone)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [amountTouched, setAmountTouched] = useState(false)

  useEffect(() => {
    if (open) {
      setSelectedMethod(PAYMENT_METHOD_IDS.YAPPY)
      setAmount(formattedTotalAmount)
      setPhoneInput(formattedPhone)
      setSubmitError(null)
      setPhoneTouched(false)
      setAmountTouched(false)
    }
  }, [open, formattedTotalAmount, formattedPhone])

  const resetState = () => {
    setSelectedMethod(null)
    setAmount(formattedTotalAmount)
    setPhoneInput(formattedPhone)
    setSubmitError(null)
    setPhoneTouched(false)
    setAmountTouched(false)
  }

  const handleClose = () => {
    if (isSubmitting) return
    resetState()
    onClose()
  }

  const handleAmountChange = e => {
    const val = e.target.value
    if (val === '' || NUMERIC_INPUT_RE.test(val)) {
      setAmount(val)
      setAmountTouched(true)
    }
  }

  const handlePhoneChange = e => {
    setPhoneInput(sanitizeCellPhone(e.target.value))
    setPhoneTouched(true)
  }

  const handleConfirm = async () => {
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await onConfirm({
        method: selectedMethod,
        amount: Number(amount),
        phone: phoneInput,
      })
      resetState()
      onClose()
    } catch (error) {
      console.error('Error al confirmar método de pago:', error)
      const apiMessage = error?.response?.data?.Message
      setSubmitError(apiMessage || t('PAYMENT_METHOD_MODAL_T.GENERIC_ERROR'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const amountNum = Number(amount)
  const maxRounded =
    formattedTotalAmount === '' ? null : Number(formattedTotalAmount)
  const amountValid =
    VALID_AMOUNT_RE.test(String(amount)) &&
    amountNum > 0 &&
    (maxRounded == null || amountNum <= maxRounded)
  const phoneValid = regexCellPhone.test(phoneInput)
  const isConfirmEnabled =
    !!selectedMethod &&
    amountValid &&
    (selectedMethod !== PAYMENT_METHOD_IDS.YAPPY || phoneValid) &&
    !isSubmitting

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="payment-method-modal-title"
      aria-describedby="payment-method-modal-subtitle"
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.2)' },
        },
      }}>
      <Box className="payment-method-modal" data-testid="modal-payment-method">
        <div className="payment-method-modal__header">
          <h2
            id="payment-method-modal-title"
            className="payment-method-modal__title">
            {t('PAYMENT_METHOD_MODAL_T.TITLE')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="payment-method-modal__close-btn"
            aria-label={t('PAYMENT_METHOD_MODAL_T.CLOSE_ARIA_LABEL')}
            disabled={isSubmitting}
            data-testid="btn-close-payment-modal">
            ✕
          </button>
        </div>

        <p
          id="payment-method-modal-subtitle"
          className="payment-method-modal__subtitle">
          {t('PAYMENT_METHOD_MODAL_T.SUBTITLE')}
        </p>

        <fieldset className="payment-method-modal__options">
          <legend className="payment-method-modal__options-legend">
            {t('PAYMENT_METHOD_MODAL_T.OPTIONS_LEGEND')}
          </legend>
          {fullWidthOption && (
            <PaymentOptionCard
              option={fullWidthOption}
              isSelected={selectedMethod === fullWidthOption.id}
              onSelect={setSelectedMethod}
              disabled={!fullWidthOption.enabled}
            />
          )}
          <div className="payment-method-modal__options-grid">
            {gridOptions.map(option => (
              <PaymentOptionCard
                key={option.id}
                option={option}
                isSelected={selectedMethod === option.id}
                onSelect={setSelectedMethod}
                disabled={!option.enabled}
              />
            ))}
          </div>
        </fieldset>

        <div className="payment-method-modal__amount-section">
          <label
            htmlFor="payment-method-amount"
            className="payment-method-modal__field-label">
            {t('PAYMENT_METHOD_MODAL_T.AMOUNT_LABEL')}
          </label>
          <div className="payment-method-modal__amount-wrapper">
            <span className="payment-method-modal__amount-prefix">$</span>
            <input
              id="payment-method-amount"
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              className="payment-method-modal__amount-input"
              placeholder={t('PAYMENT_METHOD_MODAL_T.AMOUNT_PLACEHOLDER')}
              disabled={isSubmitting}
              data-testid="input-payment-amount"
            />
          </div>
          <p className="payment-method-modal__field-hint">
            {t('PAYMENT_METHOD_MODAL_T.AMOUNT_HINT')}
          </p>
          {amountTouched && !amountValid && (
            <p
              className="payment-method-modal__field-error"
              role="alert"
              data-testid="amount-field-error">
              {t('PAYMENT_METHOD_MODAL_T.AMOUNT_ERROR')}
            </p>
          )}
        </div>

        <div className="payment-method-modal__phone-section">
          <label
            htmlFor="payment-method-phone"
            className="payment-method-modal__field-label">
            {t('PAYMENT_METHOD_MODAL_T.PHONE_LABEL')}
          </label>
          <input
            id="payment-method-phone"
            type="text"
            inputMode="numeric"
            value={phoneInput}
            onChange={handlePhoneChange}
            className="payment-method-modal__phone-input"
            placeholder={t('PAYMENT_METHOD_MODAL_T.PHONE_PLACEHOLDER')}
            maxLength={8}
            disabled={isSubmitting}
            data-testid="input-payment-phone"
          />
          {phoneTouched && !phoneValid && (
            <p
              className="payment-method-modal__field-error"
              role="alert"
              data-testid="phone-field-error">
              {t('PAYMENT_METHOD_MODAL_T.PHONE_ERROR')}
            </p>
          )}
        </div>

        {submitError && (
          <div
            className="payment-method-modal__error"
            role="alert"
            data-testid="payment-method-error">
            <ExclamationTriangleFill className="payment-method-modal__error-icon" />
            <span>{submitError}</span>
          </div>
        )}

        <div className="payment-method-modal__footer">
          <button
            type="button"
            onClick={handleClose}
            className="payment-method-modal__cancel-btn"
            disabled={isSubmitting}
            data-testid="btn-cancel-payment-method">
            {t('PAYMENT_METHOD_MODAL_T.CANCEL_BUTTON')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="payment-method-modal__confirm-btn"
            disabled={!isConfirmEnabled}
            data-testid="btn-confirm-payment-method">
            {isSubmitting
              ? t('PAYMENT_METHOD_MODAL_T.PROCESSING_BUTTON')
              : t('PAYMENT_METHOD_MODAL_T.CONFIRM_BUTTON')}
          </button>
        </div>
      </Box>
    </Modal>
  )
}

PaymentMethodModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  totalAmount: PropTypes.number,
  phone: PropTypes.string,
}

export default PaymentMethodModal
